/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { format, Heading, IModuleProps, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import { computed } from 'mobx';
import * as React from 'react';
import { isChannelTypeB2B } from '@msdyn365-commerce/core';

import { IB2bRequestsStatusData } from './b2b-requests-status.data';
import { IB2bRequestsStatusProps, IHeadingData } from './b2b-requests-status.props.autogenerated';
import { B2bRequestsTable } from './components/b2b-requests-table';

export interface IB2bRequestsStatusViewProps extends IB2bRequestsStatusProps<IB2bRequestsStatusData> {
    b2bRequestsStatus: IModuleProps;
    heading?: React.ReactNode;
    table: React.ReactNode;
}

/**
 *
 * B2bRequestsStatus component.
 * @extends {React.PureComponent<IB2bRequestsStatusProps<IB2bRequestsStatusData>>}
 */
class B2bRequestsStatus extends React.PureComponent<IB2bRequestsStatusProps<IB2bRequestsStatusData>> {
    @computed get isMobile(): boolean {
        const size = isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
        return size === 'xs';
    }

    public render(): JSX.Element | null {
        const { config } = this.props;

        if (!isChannelTypeB2B(this.props.context.actionContext.requestContext)) {
            return null;
        }

        const className = config.className || '';

        const viewProps = {
            ...this.props,
            b2bRequestsStatus: {
                moduleProps: this.props,
                className
            },
            heading: this.renderHeader(className, config.heading),
            table: this.renderTable()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly renderHeader = (className: string, headingData: IHeadingData): React.ReactNode => {
        const itemCount = this.props.data.operationRequests.result?.length ?? 0;

        return (
            <div className={`${className}__header__container`}>
                <Heading className={`${className}__header__heading`} tag={headingData.tag} text={headingData.text} />
                <span>{this.getItemNumberDisplayString(itemCount)}</span>
            </div>
        );
    };

    /**
     * Returns columns that needs to be excluded for mobile view.
     * @returns Array of column names.
     */
    private readonly _retrieveExcludedColumns = (): string[] => {
        const { resources } = this.props;
        return [resources.b2bRequestsRequestDateColumn, resources.b2bRequestsDescriptionColumn, resources.b2bRequestsUserColumn];
    };

    private readonly renderTable = (): React.ReactNode => {
        return (
            <B2bRequestsTable moduleProps={this.props} isMobile={this.isMobile} mobileExcludedColumns={this._retrieveExcludedColumns()} />
        );
    };

    private readonly getItemNumberDisplayString = (itemCount: number): string => {
        switch (itemCount) {
            case 0:
                return '';
            case 1:
                return format(this.props.resources.b2bRequestsNumberOfItemsSingular, itemCount);
            default:
                return format(this.props.resources.b2bRequestsNumberOfItems, itemCount);
        }
    };
}

export default B2bRequestsStatus;
